"use client";

import { AnimatePresence, m, type Transition } from "motion/react";
import { useId, useState } from "react";
import DemoFooter from "@/components/preview/demo-footer";
import DemoWrapper from "@/components/preview/demo-wrapper";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const LABEL_TRANSITION: Transition = { duration: 0.2, ease: "easeOut" };

const TEXT =
  "Designing interfaces that feel natural and intuitive for everyone who uses them.";

function Paragraph({
  expanded,
  ...props
}: React.ComponentProps<"p"> & { expanded: boolean }) {
  return (
    <p
      className={cn(
        "w-50 max-w-full font-medium text-base text-gray-1200 leading-snug",
        !expanded && "line-clamp-2"
      )}
      {...props}
    >
      {TEXT}
    </p>
  );
}

export default function TypographyTruncationDemo() {
  const textId = useId();
  const [expanded, setExpanded] = useState(false);
  const label = expanded ? "Less" : "More";

  return (
    <DemoWrapper margin="">
      <div className="flex min-h-40 w-full items-center justify-center px-6 py-8">
        {expanded ? (
          <Paragraph expanded id={textId} />
        ) : (
          <TooltipProvider delay={200}>
            <Tooltip
              label={TEXT}
              popupClassName="max-w-64 text-pretty"
              render={<Paragraph expanded={false} id={textId} />}
              sideOffset={6}
            />
          </TooltipProvider>
        )}
      </div>

      <DemoFooter>
        <Button
          aria-controls={textId}
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          rounded
          size="sm"
          type="button"
          variant="secondary"
        >
          <AnimatePresence initial={false} mode="popLayout">
            <m.span
              animate={{ filter: "blur(0px)", opacity: 1 }}
              className="whitespace-nowrap will-change-transform"
              exit={{ filter: "blur(4px)", opacity: 0 }}
              initial={{ filter: "blur(4px)", opacity: 0 }}
              key={label}
              transition={LABEL_TRANSITION}
            >
              {label}
            </m.span>
          </AnimatePresence>
        </Button>
      </DemoFooter>
    </DemoWrapper>
  );
}
